import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api';
import { RUN_SORTS, runsApi, type RunAgentOption, type RunListResult, type RunRow, type RunSort, type RunStatus } from '../api/runs';
import { tasksApi, type TaskBusiness } from '../api/tasks';
import { Table, type Column, type TableSort } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { RUN_ERROR_KEY, RUN_STATUS_KEY, fmtCost, fmtDuration, runStatusClass } from './runs.format';
import { fmtWhen } from './tasks.format';
import './runs.css';

const PAGE_SIZE = 25;
/** Status chips of the mockup; `waiting_review` and `partially_completed` are not entered in phase 2. */
const CHIP_STATUSES: RunStatus[] = ['queued', 'running', 'waiting_approval', 'retrying', 'completed', 'failed', 'cancelled'];
type Period = 'today' | 'week' | 'month' | 'all';
const PERIODS: Period[] = ['today', 'week', 'month', 'all'];
const PERIOD_KEY = { today: 'runs.filter.today', week: 'runs.filter.week', month: 'runs.filter.month', all: 'runs.filter.all' } as const;

/** Half-open window `[from, to)` in the browser's local time; "all" means no bound at all. */
function windowOf(period: Period): { from?: string; to?: string } {
  if (period === 'all') return {};
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (period === 'today') return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(), to: to.toISOString() };
  if (period === 'week') return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6).toISOString(), to: to.toISOString() };
  return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: to.toISOString() };
}

export function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
  const key = RUN_ERROR_KEY[code];
  return key ? t(key) : code || t('common.error');
}

/** Прогони: every agent run of the tenant, filtered and sorted on the server (`GET /runs`). */
export function Runs() {
  const [businesses, setBusinesses] = useState<TaskBusiness[]>([]);
  const [agents, setAgents] = useState<RunAgentOption[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [agentInstanceId, setAgentInstanceId] = useState('');
  const [period, setPeriod] = useState<Period>('month');
  const [chip, setChip] = useState<RunStatus | 'all'>('all');
  const [sort, setSort] = useState<TableSort | null>(null);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<RunListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Every list request gets a sequence number; a response that is not the latest one is dropped.
  const seq = useRef(0);

  useEffect(() => {
    tasksApi.businesses().then(setBusinesses).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    runsApi.agents(businessId || undefined).then(setAgents).catch((e) => setError(errorText(e)));
  }, [businessId]);

  useEffect(() => {
    setPage(1);
  }, [businessId, agentInstanceId, period, chip, sort]);

  useEffect(() => {
    setError(null);
    const mine = ++seq.current;
    const w = windowOf(period);
    runsApi
      .list({
        businessId: businessId || undefined,
        agentInstanceId: agentInstanceId || undefined,
        status: chip === 'all' ? undefined : [chip],
        ...w,
        sort: sort ? (sort.key as RunSort) : undefined,
        dir: sort?.dir,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((r) => {
        if (mine === seq.current) setResult(r);
      })
      .catch((e) => {
        if (mine === seq.current) setError(errorText(e));
      });
  }, [businessId, agentInstanceId, period, chip, sort, page]);

  // An agent whose instance was filtered away by a business switch must not keep filtering the list.
  useEffect(() => {
    if (agentInstanceId && agents.length > 0 && !agents.some((a) => a.id === agentInstanceId)) setAgentInstanceId('');
  }, [agents, agentInstanceId]);

  const columns = useMemo<Column<RunRow>[]>(
    () => [
      { key: 'task', title: t('runs.col.task'), render: (r) => (r.task ? <Link to={`/runs/${r.id}`}>{r.task.key}</Link> : <Link to={`/runs/${r.id}`} className="muted">{t('runs.detail.noTask')}</Link>) },
      { key: 'agent', title: t('runs.col.agent'), render: (r) => r.agent.name, wrap: true },
      { key: 'model', title: t('runs.col.model'), render: (r) => r.model, sortable: true },
      { key: 'status', title: t('runs.col.status'), render: (r) => <span className={runStatusClass(r.status)}>{t(RUN_STATUS_KEY[r.status])}</span>, sortable: true },
      { key: 'cost', title: t('runs.col.cost'), render: (r) => <span className="num">{fmtCost(r.cost)} {r.currency}</span>, sortable: true },
      { key: 'duration', title: t('runs.col.duration'), render: (r) => fmtDuration(r.durationSec) },
      { key: 'startedAt', title: t('runs.col.started'), render: (r) => <span className="muted">{fmtWhen(r.startedAt ?? r.createdAt)}</span>, sortable: true },
      { key: 'turns', title: t('runs.col.turns'), render: (r) => <span className="num">{r.turns}</span> },
    ],
    [],
  );

  const total = result?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="runs">
      <h1>{t('runs.title')}</h1>
      <p className="muted">{t('runs.intro')}</p>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}

      <div className="row filters" style={{ margin: 'var(--sp-4) 0 var(--sp-2)' }}>
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('runs.filter.allBusinesses')}>
          <option value="">{t('runs.filter.allBusinesses')}</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <select value={agentInstanceId} onChange={(e) => setAgentInstanceId(e.target.value)} aria-label={t('runs.filter.allAgents')}>
          <option value="">{t('runs.filter.allAgents')}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <select value={period} onChange={(e) => setPeriod(e.target.value as Period)} aria-label={t('runs.filter.period')}>
          {PERIODS.map((p) => (
            <option key={p} value={p}>{t(PERIOD_KEY[p])}</option>
          ))}
        </select>
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
        <button type="button" className={`chip${chip === 'all' ? ' on' : ''}`} onClick={() => setChip('all')} aria-pressed={chip === 'all'}>{t('runs.chip.all')}</button>
        {CHIP_STATUSES.map((s) => (
          <button key={s} type="button" className={`chip${chip === s ? ' on' : ''}`} onClick={() => setChip(s)} aria-pressed={chip === s}>
            {t(RUN_STATUS_KEY[s])}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        rows={result?.rows ?? []}
        rowKey={(r) => r.id}
        empty={result === null ? t('common.loading') : t('runs.empty')}
        sort={sort}
        onSort={(s) => setSort(s && RUN_SORTS.includes(s.key as RunSort) ? s : null)}
      />

      <div className="row" style={{ marginTop: 'var(--sp-3)', justifyContent: 'space-between' }}>
        <span className="muted">{t('runs.shown', { shown: result?.rows.length ?? 0, total, page })}</span>
        {pages > 1 && (
          <span className="row">
            <button type="button" className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('runs.page.prev')}</button>
            <button type="button" className="btn secondary small" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>{t('runs.page.next')}</button>
          </span>
        )}
      </div>
    </div>
  );
}
